import React, { ReactNode, useCallback, useEffect } from 'react';
import { batch } from 'react-redux';
import {
  restoreInitialConfig,
  setBuilderSchemaId,
} from '../../../redux/slices/oldBuilder';
import * as BuilderHeaderUI from './BuilderHeaderUI';
import SchemaDropdownMenu from '../../SchemaDropdownMenu';
import { PermissionKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import { activeSchemaIdSelector } from '../../../redux/selectors/builder';
import { useNavigate } from 'react-router-dom';
import { getQueryTypePagePath } from '../../../utils';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import usePermissionSchemas from '../../../pages/PermissionControls/hooks/usePermissionSchemas';
import { SecondaryButton } from '../../Button';
import {
  clearCreateNewReport,
  clearReplaceReport,
} from '../../../redux/slices/cachedEntities';
import useCachedEntitiesSelector from '../../../redux/selectorHooks/useCachedEntitiesSelector';
import KubitTooltip from '../../KubitTooltip';

interface IProps {
  queryType: QueryType;
  children?: ReactNode;
  actions?: ReactNode;
}

export default ({ queryType, children, actions }: IProps) => {
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    defaultSchemaId: activeSchemaId,
    requestedPermissionKey:
      queryType === QueryType.CohortSample
        ? PermissionKey.CohortReportRead
        : PermissionKey.AnalysisRead,
  });

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const createNewReport = useCachedEntitiesSelector('createNewReport');
  const replaceReport = useCachedEntitiesSelector('replaceReport');

  const handleReloadPage = useCallback(() => {
    navigate(getQueryTypePagePath(queryType));
  }, [navigate, queryType]);

  const handleBuilderReset = () => {
    batch(() => {
      dispatch(restoreInitialConfig());

      if (replaceReport) {
        dispatch(clearReplaceReport());
      }

      if (createNewReport) {
        dispatch(clearCreateNewReport());
      }
    });

    handleReloadPage();
  };

  const handleChangeSchemaId = useCallback(
    (newSchemaId: string) => {
      dispatch(setBuilderSchemaId({ schemaId: newSchemaId }));

      handleReloadPage();
    },
    [dispatch, handleReloadPage],
  );

  // If the selected schema after permission check is diff to the
  // build active schema, then change the active schema to the selected schema
  // from usePermissionSchema hook
  useEffect(() => {
    if (selectedSchemaId !== activeSchemaId) {
      handleChangeSchemaId(selectedSchemaId);
    }
  }, [handleChangeSchemaId, selectedSchemaId, activeSchemaId]);

  return (
    <BuilderHeaderUI.Container>
      <BuilderHeaderUI.SettingsContainer>
        <SchemaDropdownMenu
          value={selectedSchemaId}
          onChange={handleChangeSchemaId}
          permittedSchemaIds={permittedSchemaIds}
        />
        {children}
      </BuilderHeaderUI.SettingsContainer>
      <BuilderHeaderUI.HeaderActionsContainer>
        {actions}
        <KubitTooltip content={'Clear all inputs.'} placement="bottom">
          <SecondaryButton
            type="button"
            onClick={handleBuilderReset}
            isOutlined
            trackContext="builder"
            trackAction="reset"
          >
            Reset
          </SecondaryButton>
        </KubitTooltip>
      </BuilderHeaderUI.HeaderActionsContainer>
    </BuilderHeaderUI.Container>
  );
};
